import axiosInstance from '@/lib/api/axios-instance';
import type {
  HubspotDealDetail,
  OpportunitiesFacets,
  OpportunitiesFilters,
  OpportunitiesListResponse,
  OpportunitiesSyncStatus,
  PipelinesResponse,
} from '../types/opportunity.types';
import type { HubspotSyncJobStatus } from '@/lib/hubspot-sync';

function buildParams(filters: OpportunitiesFilters): URLSearchParams {
  const p = new URLSearchParams();
  filters.stage?.forEach((s) => p.append('stage', s));
  filters.pipeline?.forEach((s) => p.append('pipeline', s));
  filters.ownerId?.forEach((s) => p.append('ownerId', s));
  if (filters.hubspotCompanyId) p.set('hubspotCompanyId', filters.hubspotCompanyId);
  if (filters.search) p.set('search', filters.search);
  if (filters.closeDateFrom) p.set('closeDateFrom', filters.closeDateFrom);
  if (filters.closeDateTo) p.set('closeDateTo', filters.closeDateTo);
  if (filters.amountMin !== undefined) p.set('amountMin', String(filters.amountMin));
  if (filters.amountMax !== undefined) p.set('amountMax', String(filters.amountMax));
  if (filters.openOnly) p.set('openOnly', 'true');
  p.set('page', String(filters.page));
  p.set('pageSize', String(filters.pageSize));
  p.set('sortBy', filters.sortBy);
  return p;
}

export const opportunitiesService = {
  async list(filters: OpportunitiesFilters): Promise<OpportunitiesListResponse> {
    const { data } = await axiosInstance.get<OpportunitiesListResponse>('/api/hubspot-deals', {
      params: buildParams(filters),
    });
    return data;
  },
  async detail(id: string): Promise<HubspotDealDetail> {
    const { data } = await axiosInstance.get<{ data: HubspotDealDetail }>(
      `/api/hubspot-deals/${id}`
    );
    return data.data;
  },
  async facets(): Promise<OpportunitiesFacets> {
    const { data } = await axiosInstance.get<OpportunitiesFacets>('/api/hubspot-deals/facets');
    return data;
  },
  async pipelines(): Promise<PipelinesResponse> {
    const { data } = await axiosInstance.get<PipelinesResponse>('/api/hubspot-deals/pipelines/all');
    return data;
  },
  async syncStatus(): Promise<OpportunitiesSyncStatus> {
    const { data } = await axiosInstance.get<{ data: OpportunitiesSyncStatus }>(
      '/api/hubspot-deals/sync-status'
    );
    return data.data;
  },
  async startSync(): Promise<{ jobId: string; alreadyRunning: boolean }> {
    const { data } = await axiosInstance.post<{ data: { jobId: string; alreadyRunning: boolean } }>(
      '/api/hubspot-deals/sync'
    );
    return data.data;
  },
  async syncJob(jobId: string): Promise<HubspotSyncJobStatus> {
    const { data } = await axiosInstance.get<{ data: HubspotSyncJobStatus }>(
      `/api/hubspot-deals/sync/${encodeURIComponent(jobId)}`
    );
    return data.data;
  },
};
